// src/services/dailyRewards.ts
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import { db, functions } from '../lib/firebase';

export type DailyTaskActionId = 'log_catch' | 'share_to_bay' | 'equip_gear' | 'search_area' | 'open_fishiq';

export type DailyRewardCosmeticAward = {
  cosmeticId: string;
  slot: string;
  name: string;
  rarity: string;
  emoji?: string | null;
  grantSource?: string;
  source?: string;
  cycleDay?: number;
  streak?: number;
};

export type DailyTaskPreview = {
  schemaVersion?: number;
  date: string;
  taskId: string;
  actionId: DailyTaskActionId;
  title: string;
  description: string;
  rewardShells: number;
  completed?: boolean;
  completedAtMs?: number | null;
  completedRewardShells?: number | null;
};

export type DailyRewardResult = {
  ok: boolean;
  alreadyClaimed?: boolean;
  today?: string;
  timeZone?: string;
  awardedShells?: number;
  baseShells?: number;
  bonusShells?: number;
  shellBalance?: number;
  lifetimeShells?: number;
  streak?: number;
  bestStreak?: number;
  cycleDay?: number;
  totalClaims?: number;
  cosmeticAward?: DailyRewardCosmeticAward | null;
  awardedSearchLures?: number;
  searchLureBalance?: number;
  duplicateCosmetic?: boolean;
  dailyTask?: DailyTaskPreview | null;
  dailyRewards?: Record<string, any>;
};

export type DailyTaskActionResult = {
  ok: boolean;
  completed?: boolean;
  alreadyCompleted?: boolean;
  noActiveTask?: boolean;
  matched?: boolean;
  today?: string;
  timeZone?: string;
  actionId?: DailyTaskActionId;
  awardedShells?: number;
  shellBalance?: number;
  lifetimeShells?: number;
  dailyTask?: DailyTaskPreview | null;
  dailyRewards?: Record<string, any>;
};

export type DailyRewardPreview = {
  today: string;
  timeZone: string;
  alreadyClaimed: boolean;
  nextStreak: number;
  bestStreak: number;
  cycleDay: number;
  shells: number;
  cosmeticName?: string;
  cosmeticEmoji?: string;
  dailyTask?: DailyTaskPreview | null;
  searchLures: number;
};

function isValidTimeZone(value: unknown): value is string {
  if (typeof value !== 'string' || !value.trim()) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date());
    return true;
  } catch {
    return false;
  }
}

function ymdInTimeZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const year = parts.find((part) => part.type === 'year')?.value || '1970';
  const month = parts.find((part) => part.type === 'month')?.value || '01';
  const day = parts.find((part) => part.type === 'day')?.value || '01';
  return `${year}-${month}-${day}`;
}

const SHELL_REWARDS_BY_DAY: Record<number, number> = {
  1: 10,
  2: 12,
  3: 15,
  4: 18,
  5: 25,
  6: 28,
  7: 30,
  8: 32,
  9: 35,
  10: 50,
  11: 55,
  12: 60,
  13: 65,
  14: 70,
  15: 75,
  16: 80,
  17: 85,
  18: 90,
  19: 95,
  20: 100,
};

const COSMETIC_PREVIEW_BY_DAY: Record<number, { name: string; emoji: string }> = {
  5: { name: 'Lucky Minnow Charm', emoji: '🐟' },
  10: { name: 'Harbor Bronze Frame', emoji: '⚓' },
  15: { name: 'Marsh Scout title', emoji: '🌿' },
  20: { name: 'Sunset Surf Rod', emoji: '🌅' },
};

const DAILY_TASKS: DailyTaskPreview[] = [
  {
    date: '',
    taskId: 'log_catch',
    actionId: 'log_catch',
    title: 'Log a catch',
    description: 'Add one catch to your Roddo log today.',
    rewardShells: 25,
    completed: false,
  },
  {
    date: '',
    taskId: 'share_to_bay',
    actionId: 'share_to_bay',
    title: 'Share on Baleen Bay',
    description: 'Post a catch or fishing update for the Roddo crew.',
    rewardShells: 35,
    completed: false,
  },
  {
    date: '',
    taskId: 'equip_gear',
    actionId: 'equip_gear',
    title: 'Tune your gear',
    description: 'Equip tackle or avatar gear from The Hold.',
    rewardShells: 20,
    completed: false,
  },
  {
    date: '',
    taskId: 'search_area',
    actionId: 'search_area',
    title: 'Search the water',
    description: 'Use Search this area on the map to scout nearby fish.',
    rewardShells: 15,
    completed: false,
  },
  {
    date: '',
    taskId: 'open_fishiq',
    actionId: 'open_fishiq',
    title: 'Check FishIQ',
    description: 'Open FishIQ and review today\'s fishing read.',
    rewardShells: 15,
    completed: false,
  },
];

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

export function getRoddoLocalDate(date = new Date()): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function getRoddoTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
}

function addDaysToYmd(ymd: string, deltaDays: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return null;
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) + deltaDays));
  return date.toISOString().slice(0, 10);
}

function cycleDayForStreak(streak: number): number {
  const safeStreak = Math.max(1, Math.floor(Number.isFinite(streak) ? streak : 1));
  return ((safeStreak - 1) % 20) + 1;
}

function hashString(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function getDailyTaskForDate(uid: string, today: string): DailyTaskPreview {
  const index = hashString(`${uid}:${today}:roddo-daily-task-v1`) % DAILY_TASKS.length;
  return { ...(DAILY_TASKS[index] || DAILY_TASKS[0]), date: today };
}

function normalizeTaskFromUser(userProfile: any, today: string): DailyTaskPreview | null {
  const task = userProfile?.dailyRewards?.dailyTask;
  if (!task || String(task.date || '') !== today) return null;

  const taskId = String(task.taskId || '').trim();
  const fallback = DAILY_TASKS.find((item) => item.taskId === taskId);
  if (!fallback && !taskId) return null;

  return {
    ...(fallback || getDailyTaskForDate(String(userProfile?.uid || ''), today)),
    ...task,
    date: today,
    taskId: taskId || fallback?.taskId || 'log_catch',
    actionId: (task.actionId || fallback?.actionId || 'log_catch') as DailyTaskActionId,
    title: String(task.title || fallback?.title || 'Today\'s Cast task'),
    description: String(task.description || fallback?.description || 'Complete one Roddo action today.'),
    rewardShells: Math.max(1, Math.floor(Number(task.rewardShells || fallback?.rewardShells || 15))),
    completed: task.completed === true,
  };
}

export function getDailyRewardPreview(userProfile: any): DailyRewardPreview {
  const daily = userProfile?.dailyRewards || {};
  const timeZone = isValidTimeZone(daily.timeZone)
    ? String(daily.timeZone)
    : getRoddoTimeZone();
  const today = ymdInTimeZone(new Date(), timeZone);
  const lastClaimDate = String(daily.lastClaimDate || '').trim();
  const alreadyClaimed = lastClaimDate === today;
  const previousStreak = Math.max(0, Math.floor(Number(daily.streak || 0)) || 0);
  const yesterday = addDaysToYmd(today, -1);
  const nextStreak = alreadyClaimed
    ? Math.max(1, previousStreak)
    : lastClaimDate && lastClaimDate === yesterday
      ? previousStreak + 1
      : 1;
  const bestStreak = Math.max(nextStreak, Math.floor(Number(daily.bestStreak || 0)) || 0);
  const cycleDay = cycleDayForStreak(nextStreak);
  const cosmetic = COSMETIC_PREVIEW_BY_DAY[cycleDay];
  const uid = String(userProfile?.uid || '').trim();
  const dailyTask = normalizeTaskFromUser(userProfile, today) || (uid ? getDailyTaskForDate(uid, today) : null);

  return {
    today,
    timeZone,
    alreadyClaimed,
    nextStreak,
    bestStreak,
    cycleDay,
    shells: SHELL_REWARDS_BY_DAY[cycleDay] || 10,
    cosmeticName: cosmetic?.name,
    cosmeticEmoji: cosmetic?.emoji,
    dailyTask,
    searchLures: 5,
  };
}

export async function claimDailyReward(): Promise<DailyRewardResult> {
  const callable = httpsCallable(functions, 'claimDailyReward');
  const result = await callable({
    timeZone: getRoddoTimeZone(),
  });

  return (result.data || {}) as DailyRewardResult;
}

export async function completeDailyTaskAction(actionId: DailyTaskActionId): Promise<DailyTaskActionResult> {
  const callable = httpsCallable(functions, 'completeDailyTaskAction');
  const result = await callable({
    actionId,
    timeZone: getRoddoTimeZone(),
  });

  return (result.data || {}) as DailyTaskActionResult;
}

export type DailyActivityState = {
  date: string;
  timeZone?: string;
  actions: Partial<Record<DailyTaskActionId, number>>;
  lastActionId?: DailyTaskActionId | null;
  lastActionAtMs?: number | null;
  schemaVersion?: number;
};

export function subscribeToDailyActivity(
  userId: string,
  dateKey: string,
  onChange: (activity: DailyActivityState | null) => void,
  onError?: (err: unknown) => void,
): Unsubscribe {
  const uid = String(userId || '').trim();
  const date = String(dateKey || '').trim();
  if (!uid || !date) {
    onChange(null);
    return () => undefined;
  }

  return onSnapshot(
    doc(db, 'users', uid, 'dailyActivity', date),
    (snap) => {
      if (!snap.exists()) {
        onChange(null);
        return;
      }
      const data = snap.data() || {};
      onChange({
        date,
        timeZone: typeof data.timeZone === 'string' ? data.timeZone : undefined,
        actions: data.actions && typeof data.actions === 'object' ? data.actions : {},
        lastActionId: data.lastActionId || null,
        lastActionAtMs: Number(data.lastActionAtMs || 0) || null,
        schemaVersion: Number(data.schemaVersion || 1),
      });
    },
    (err) => {
      onError?.(err);
      onChange(null);
    },
  );
}

